import { AbstractControl, UntypedFormArray, UntypedFormControl, UntypedFormGroup, Validators } from "@angular/forms";

import { distinctUntilChanged } from "rxjs/operators";

import { DffFormControl, DffFormControlType } from "@dffedb/forms";
import { EntitetStatus } from "@dffedb/util";

import {
    Note,
    NoteBruger,
    NoteHenvendelsestype,
    NoteListeItem,
    NoteOmraade,
    NoteOpgavestyringStatus,
    opretTomOpgavestyring
} from "../model/note.model";
import { NoteBilagFormBuilder } from "./note-bilag-form-builder";
import { NoteOpgavestyringFormBuilder } from "./note-opgavestyring-form-builder";

export class NoteFormBuilder {
    private static ignoreUpdateStatus = false;

    public static noterToFormArray(
        noter: (Note | NoteListeItem)[],
        omraader: NoteOmraade[],
        henvendelsestyper: NoteHenvendelsestype[],
        opgavestyringStatusser: NoteOpgavestyringStatus[],
        brugere: NoteBruger[]
    ): UntypedFormArray {
        noter = noter || [];
        return new UntypedFormArray(
            noter.map((n) => this.noteToFormGroup(n, omraader, henvendelsestyper, opgavestyringStatusser, brugere))
        );
    }

    public static erNote(note: Note | NoteListeItem): note is Note {
        return note.fullyLoaded;
    }

    public static noteToFormGroup(
        note: Note | NoteListeItem,
        omraader: NoteOmraade[],
        henvendelsestyper: NoteHenvendelsestype[],
        opgavestyringStatusser: NoteOpgavestyringStatus[],
        brugere: NoteBruger[]
    ): UntypedFormGroup {
        const omraadeId = new DffFormControl(note.omraadeId, {
            placeholder: "Område",
            controlType: DffFormControlType.DropdownList,
            dropdownListOptions: [{}, ...omraader]
        });

        const henvendelseTypeId = new DffFormControl(this.erNote(note) ? note.henvendelseTypeId : 0, {
            placeholder: "Type",
            controlType: DffFormControlType.DropdownList,
            dropdownListOptions: [{}, ...this.hentHenvendelsestyper(henvendelsestyper, omraadeId.value)]
        });

        omraadeId.valueChanges.subscribe(() => {
            henvendelseTypeId.dropdownListOptions = [{}, ...this.hentHenvendelsestyper(henvendelsestyper, omraadeId.value)];
        });

        const bilag = this.erNote(note) ? note.bilag : [];
        const opgavestyring = this.erNote(note) ? note.opgavestyring : opretTomOpgavestyring();
        const status = this.erNote(note) ? note.status : 0;
        const tekst = this.erNote(note) ? note.tekst : "";
        const result = new UntypedFormGroup({
            fullyLoaded: new UntypedFormControl(note.fullyLoaded),
            antalBilag: new UntypedFormControl(note.antalBilag),
            bilag: NoteBilagFormBuilder.bilagToFormArray(bilag),
            dato: new UntypedFormControl(note.dato),
            henvendelseTypeId,
            noteId: new UntypedFormControl(note.noteId),
            omraadeId,
            opgavestyring: NoteOpgavestyringFormBuilder.opgavestyringToFormGroup(opgavestyring, opgavestyringStatusser, brugere),
            overskrift: new DffFormControl(note.overskrift, { placeholder: "Overskrift", controlType: DffFormControlType.TextInput }),
            status: new UntypedFormControl(status),
            tekst: new UntypedFormControl(tekst)
        });

        if (note.noteId !== 0) {
            result.controls["omraadeId"].disable();
        }

        // Hvis det kun er muligt at vælge ét område, og det allerede er valgt, disabler vi feltet
        if (omraader.length === 1 && note.omraadeId === omraader[0].key) {
            result.controls["omraadeId"].disable();
        }

        this.vaelgDefaultHenvendelsestype(result, henvendelsestyper);

        // Opdater status, hvis der sker ændringer på noten.
        this.updateStatus(result);
        result.valueChanges.subscribe(() => this.updateStatus(result));

        // Ved ændring af Område, ønsker vi at sætte en default Henvendelsestype
        result.valueChanges.pipe(distinctUntilChanged((a: Note, b: Note) => a.omraadeId === b.omraadeId)).subscribe(() => {
            this.vaelgDefaultHenvendelsestype(result, henvendelsestyper);
        });

        return result;
    }

    public static formArrayToNoter(array: UntypedFormArray): Note[] {
        return array.controls.map((c) => this.formGroupToNote(c as UntypedFormGroup));
    }

    public static formGroupToNote(group: UntypedFormGroup): Note {
        const bilag = NoteBilagFormBuilder.formArrayToBilag(group.controls["bilag"] as UntypedFormArray);
        const opgavestyring = NoteOpgavestyringFormBuilder.formGroupToOpgavestyring(group.controls["opgavestyring"] as UntypedFormGroup);
        let status = group.controls["status"].value as EntitetStatus;
        if (status === EntitetStatus.Uaendret && group.dirty) {
            status = EntitetStatus.Aendret;
        }

        return {
            fullyLoaded: group.controls["fullyLoaded"].value,
            get antalBilag(): number {
                return bilag.filter((b) => b.status !== EntitetStatus.Slettet).length;
            },
            bilag,
            dato: group.controls["dato"].value,
            henvendelseTypeId: group.controls["henvendelseTypeId"].value,
            noteId: group.controls["noteId"].value,
            omraadeId: group.controls["omraadeId"].value,
            opgavestyring,
            overskrift: group.controls["overskrift"].value,
            status,
            tekst: group.controls["tekst"].value
        };
    }

    public static hentHenvendelsestyper(henvendelsestyper: NoteHenvendelsestype[], omraadeId: number): NoteHenvendelsestype[] {
        return (henvendelsestyper || []).filter((t) => omraadeId && t.omraadeId === omraadeId);
    }

    public static setControls(target: UntypedFormGroup, controls: { [key: string]: AbstractControl }): void {
        if (controls === target.controls) {
            return;
        }

        // Vi starter med 'pristine' og 'status=Uaendret'
        target.markAsPristine();
        target.controls["status"].setValue(EntitetStatus.Uaendret);

        // Kopier controls over
        // Vi ønsker at beholde pristine/dirty fra sourcen, hvorfor vi kortslutter updateStatus()
        this.ignoreUpdateStatus = true;
        try {
            // Sæt properties
            const keys = Object.keys(controls);
            for (const index in keys) {
                if (keys.hasOwnProperty(index)) {
                    target.setControl(keys[index], controls[keys[index]]);
                }
            }
        } finally {
            this.ignoreUpdateStatus = false;
        }
    }

    private static updateStatus(noteFormGroup: UntypedFormGroup): void {
        this.updateValidators(noteFormGroup);

        if (this.ignoreUpdateStatus) {
            return;
        }

        const status = noteFormGroup.get("status") as UntypedFormControl;
        if (status.value !== EntitetStatus.Uaendret) {
            status.markAsDirty();
            return;
        }

        if (noteFormGroup.dirty) {
            status.setValue(EntitetStatus.Aendret);
            status.markAsDirty();
            return;
        }

        const bilag = noteFormGroup.get("bilag") as UntypedFormArray;
        const bilagAendret = bilag.controls.some((b) => b.get("status").value !== EntitetStatus.Uaendret);
        if (bilagAendret) {
            status.setValue(EntitetStatus.Aendret);
            status.markAsDirty();
        }
    }

    private static updateValidators(formGroup: UntypedFormGroup): void {
        const status = formGroup.get("status") as UntypedFormControl;
        const slettet = status.value === EntitetStatus.Slettet;

        const omraadeId = formGroup.get("omraadeId");
        const henvendelseTypeId = formGroup.get("henvendelseTypeId");
        const overskrift = formGroup.get("overskrift");

        const omraadeIdValidators = slettet ? [] : [Validators.required];
        const henvendelseTypeIdValidators = slettet ? [] : this.erNote(formGroup.value) ? [Validators.required] : [];
        const overskriftValidators = slettet ? [] : [Validators.required, Validators.maxLength(100)];

        this.setValidators(omraadeId, omraadeIdValidators);
        this.setValidators(henvendelseTypeId, henvendelseTypeIdValidators);
        this.setValidators(overskrift, overskriftValidators);
    }

    private static setValidators(
        control: AbstractControl,
        validators: ((control: AbstractControl) => import("@angular/forms").ValidationErrors)[]
    ): void {
        // Når man ændrer validatorer, bør man efterfølgende kalde updateValueAndValidity.
        // Ellers risikerer vi at få fejlen ExpressionChangedAfterItHasBeenCheckedError.
        control.setValidators(validators);
        control.updateValueAndValidity({ emitEvent: false });
    }

    private static vaelgDefaultHenvendelsestype(noteFormGroup: UntypedFormGroup, henvendelsestyper: NoteHenvendelsestype[]): void {
        const omraadeId = noteFormGroup.controls["omraadeId"];
        const henvendelseTypeId = noteFormGroup.controls["henvendelseTypeId"];

        const omraadeHenvendelsestyper = this.hentHenvendelsestyper(henvendelsestyper, omraadeId.value);
        const defaultHenvendelseType = omraadeHenvendelsestyper.length === 1 ? omraadeHenvendelsestyper[0] : null;
        const aktuelHenvendelseType = omraadeHenvendelsestyper.find((t) => t.key === henvendelseTypeId.value);
        const henvendelseType = aktuelHenvendelseType !== undefined ? aktuelHenvendelseType : defaultHenvendelseType;
        henvendelseTypeId.setValue(henvendelseType && henvendelseType.key);

        // Hvis det kun er muligt at vælge ét område og én henvendelsestype, disabler vi feltet.
        if (omraadeId.disabled && omraadeHenvendelsestyper.length === 1 && henvendelseTypeId.value === omraadeHenvendelsestyper[0].key) {
            henvendelseTypeId.disable();
        } else {
            henvendelseTypeId.enable();
        }
    }
}
